import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { PlusCircle, List as ListIcon, Eye, Printer } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import StatusBadge from "../../../../components/common/StatusBadge";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import { LpoPurposeIcon, LpoPurposePill } from "../lpoPurpose";
import DateRangeFilter, { useDateRangeFilter } from "../../components/list/DateRangeFilter";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { makeGrandTotalSummary } from "../../components/list/GrandTotalSummary";
import { sumField } from "../../components/list/listExportUtils";
import { useListLposQuery, useLazyGetLpoQuery } from "../../stockApi";
import { useCanCreateLpo, useCanPrintLpo } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import { useLazyGetCompanyInfoQuery } from "../../../settings/settingsApi";
import { useLazyGetEmployeeSignatureQuery } from "../../../masters/employees/employeesApi";
import { useLazyGetMaintenanceRequestQuery } from "../../../masters/vehicles/vehiclesApi";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import { buildLpoPrintProps, blobToDataUrl, LPO_PRINTABLE_STATUSES } from "../buildLpoPrintProps";
import { LpoInvoiceCell, LpoPaymentCell, lpoInvoiceText, lpoPaymentText } from "../lpoCycle";

// "PENDING" is a pseudo-status, not a server value: an LPO awaiting approval is
// either SUBMITTED or REVISED_SUBMITTED, and the dashboard's "Pending LPOs"
// figure counts both. Without it, a drill-down from that figure could only
// pre-select one of the two halves. Resolved client-side below.
export const PENDING_LPO_STATUSES = ["SUBMITTED", "REVISED_SUBMITTED"];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "PENDING", label: "Pending Approval" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "REVISED_SUBMITTED", label: "Revised - Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "PARTIALLY_RECEIVED", label: "Partially Received" },
  { value: "FULLY_RECEIVED", label: "Fully Received" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const PURPOSE_OPTIONS = [
  { value: "", label: "All Purposes" },
  { value: "GENERAL_REPLENISHMENT", label: "General Replenishment" },
  { value: "VEHICLE_MAINTENANCE", label: "Vehicle Maintenance" },
  { value: "CUSTOMER_SPECIFIC", label: "Customer Specific" },
  { value: "OTHER", label: "Other" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "", label: "All Payment Types" },
  { value: "CASH", label: "Cash" },
  { value: "CREDIT", label: "Credit" },
];

const LpoList = () => {
  const canCreate = useCanCreateLpo();
  const canPrint = useCanPrintLpo();
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get("status");
  const urlFrom = searchParams.get("dateFrom");
  const urlTo = searchParams.get("dateTo");

  const [status, setStatus] = useState(urlStatus || "");
  const [purposeType, setPurposeType] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const { dateFrom, setDateFrom, dateTo, setDateTo } = useDateRangeFilter();

  // A dashboard drill-down arrives with the filter behind the figure it was
  // clicked from; adopt it so the list shows the same set the figure counted,
  // rather than everything. Same shape as CollectionsListPage's adoption of a
  // One Stop drill window. The dropdowns stay editable afterwards.
  useEffect(() => {
    if (urlStatus) setStatus(urlStatus);
    if (urlFrom) setDateFrom(urlFrom);
    if (urlTo) setDateTo(urlTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStatus, urlFrom, urlTo]);

  const [fetchLpo] = useLazyGetLpoQuery();
  const [fetchCompanyInfo] = useLazyGetCompanyInfoQuery();
  const [fetchSignature] = useLazyGetEmployeeSignatureQuery();
  const [fetchVehicleRequest] = useLazyGetMaintenanceRequestQuery();
  const [printProps, setPrintProps] = useState(null);
  const [printingGuid, setPrintingGuid] = useState(null);
  // Separate from printingGuid (which only tracks the in-flight fetch, used
  // for the row-level spinner/disabled state) - this one needs to persist
  // for as long as the modal itself stays open, since the eMail action
  // needs to know which LPO it's sending.
  const [printedRecordGuid, setPrintedRecordGuid] = useState(null);

  const handlePrint = async (recordGuid) => {
    setPrintingGuid(recordGuid);
    try {
      const lpo = await fetchLpo(recordGuid).unwrap();
      const companyInfo = await fetchCompanyInfo(lpo.branchId).unwrap();
      let signatureImageUrl = null;
      if (lpo.approvedByEmployeeRecordGuid) {
        try {
          const blob = await fetchSignature(lpo.approvedByEmployeeRecordGuid).unwrap();
          signatureImageUrl = await blobToDataUrl(blob);
        } catch {
          // No signature on file for this approver - print without one.
        }
      }
      let vehicleInfo;
      if (lpo.vehicleRequestRecordGuid) {
        try {
          const request = await fetchVehicleRequest(lpo.vehicleRequestRecordGuid).unwrap();
          vehicleInfo = { regNo: request.regNo, brand: request.brand, model: request.model };
        } catch {
          // Couldn't load the linked request - print without the vehicle line.
        }
      }
      setPrintProps(buildLpoPrintProps(lpo, companyInfo, signatureImageUrl, vehicleInfo));
      setPrintedRecordGuid(recordGuid);
    } catch (e) {
      toast.error(e?.message || "Could not load the LPO for printing");
    } finally {
      setPrintingGuid(null);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListLposQuery({
    branchId: selectedBranchId ?? undefined,
    // PENDING covers two real statuses, which the endpoint can't express in one
    // call - so it is left off the request and applied client-side instead.
    status: status && status !== "PENDING" ? status : undefined,
    purposeType: purposeType || undefined,
    paymentType: paymentType || undefined,
    search: search || undefined,
    pageSize: 100,
  });

  // Client-side over what's already fetched (the query itself is unfiltered
  // by date) - both bounds start blank (useDateRangeFilter), so every
  // fetched LPO shows by default; narrowing is opt-in via these inputs.
  const filteredItems = (data?.items || []).filter((r) => {
    if (status === "PENDING" && !PENDING_LPO_STATUSES.includes(r.status)) return false;
    if (!r.lpoDate) return true;
    const d = dayjs(r.lpoDate).format("YYYY-MM-DD");
    return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const exportColumns = [
    { header: "LPO No", value: (r) => r.lpoNumber },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Supplier", value: (r) => r.supplierName },
    { header: "Purpose", value: (r) => r.purposeType?.replaceAll("_", " ") },
    { header: "Payment Type", value: (r) => (r.paymentType === "CREDIT" ? "Credit" : "Cash") },
    { header: "Date", value: (r) => (r.lpoDate ? new Date(r.lpoDate).toLocaleDateString() : "-") },
    { header: "Net Amount", value: (r) => Number(r.netAmount || 0).toFixed(2), align: "right" },
    { header: "Status", value: (r) => r.status?.replaceAll("_", " ") },
    { header: "Invoice", value: lpoInvoiceText },
    { header: "Payment", value: lpoPaymentText },
  ];
  const exportTotals = { "Net Amount": sumField(filteredItems, "netAmount").toFixed(2) };

  const columns = [
    {
      // Who it is for leads (2026-09-12): the customer on a customer-specific
      // LPO, otherwise the supplier. The purpose icon/colour reads before any
      // text; the LPO number is the link underneath.
      title: "LPO",
      dataIndex: "lpoNumber",
      render: (value, record) => {
        const forCustomer = record.purposeType === "CUSTOMER_SPECIFIC" && record.customerName;
        return (
          <PrimaryCell
            leading={<LpoPurposeIcon purposeType={record.purposeType} />}
            primary={forCustomer ? record.customerName : record.supplierName}
            secondary={value}
            to={paths.stockLpoDetail(record.recordGuid)}
            extra={
              forCustomer
                ? `Supplier: ${record.supplierName}`
                : record.purposeType === "VEHICLE_MAINTENANCE" && record.vehicleRequestNumber
                  ? record.vehicleRequestNumber
                  : null
            }
          />
        );
      },
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Purpose", dataIndex: "purposeType", render: (v) => <LpoPurposePill purposeType={v} /> },
    { title: "Payment Type", dataIndex: "paymentType", render: (v) => (v === "CREDIT" ? "Credit" : "Cash") },
    { title: "Date", dataIndex: "lpoDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Net Amount", dataIndex: "netAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
    // Cycle evidence (Batch199): the supplier invoice against the LPO, and the payment
    // against it - cheque on a credit LPO, the petty cash voucher on a cash LPO.
    { title: "Invoice", dataIndex: "invoiceRef", render: (_, r) => <LpoInvoiceCell lpo={r} /> },
    { title: "Payment", dataIndex: "chequeRef", render: (_, r) => <LpoPaymentCell lpo={r} /> },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-link p-2"
            title="View"
            onClick={() => navigate(paths.stockLpoDetail(record.recordGuid))}
          >
            <Eye size={16} />
          </button>
          {canPrint && LPO_PRINTABLE_STATUSES.includes(record.status) && (
            <button
              type="button"
              className="btn btn-link p-2"
              title="Print"
              disabled={printingGuid === record.recordGuid}
              onClick={() => handlePrint(record.recordGuid)}
            >
              <Printer size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Local Purchase Orders</h4>
              <h6>Create, approve, and track LPOs</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={() => navigate(paths.stockLpoPendingVehicleRequests)}
              >
                <ListIcon className="me-2" size={16} />
                Pending Vehicle Requests
              </button>
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockLpoNew)}>
                <PlusCircle className="me-2" size={16} />
                New LPO
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search LPO No or Supplier"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                <select className="form-control form-control-sm" style={{ width: 150 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select className="form-control form-control-sm" style={{ width: 150 }} value={purposeType} onChange={(e) => setPurposeType(e.target.value)}>
                  {PURPOSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select className="form-control form-control-sm" style={{ width: 150 }} value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                  {PAYMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ListExportToolbar
                  title="Local Purchase Orders"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={filteredItems}
                  totals={exportTotals}
                  fileName="LPOs"
                  groupBy={(r) => r.supplierName || "Unknown Supplier"}
                />
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredItems}
                rowKey="recordGuid"
                loading={isLoading}
                summary={makeGrandTotalSummary(columns, filteredItems, ["netAmount"], "lpoNumber")}
              />
            </div>
          </div>
        </div>

        <StockDocumentPrintModal
          show={!!printProps}
          onClose={() => setPrintProps(null)}
          docProps={printProps || {}}
          copies={2}
          enableEmail
          lpoRecordGuid={printedRecordGuid}
        />
      </div>
    </div>
  );
};

export default LpoList;
